import { useEffect, useState } from "react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { Label } from "@/modern/components/ui/label";
import { Switch } from "@/modern/components/ui/switch";
import { Card } from "@/modern/components/ui/card";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { KeyValueModel } from "@/bryntum-lib/models/KeyValueModel";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { GRANTS } from "@/other/constants/PrivilegeTypes";

const ScheduleTaskEventsDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { userPreferences, user } = useSwivuBryntumContext();
  // Default off: prevent duplicate/overlapping task events
  const [enableRedundantTaskEvents, setEnableRedundantTaskEvents] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  useSwivuStore<KeyValueModel>({
    storeId: STORE_KEYS.boardPreferencesStore,
    subscriberFn: ({ record }) => {
      if (record?.key === PERSISTENCE_STATE_KEYS.enableRedundantTaskEvents) {
        loadSettings();
      }
    },
  });

  useEffect(() => {
    loadSettings();
    checkPermissions();
  }, [userPreferences, user]);

  const loadSettings = () => {
    if (!userPreferences) return;

    // Default "false" = prevent duplicates
    const redundantEventsSetting = userPreferences.getSetting(
      PERSISTENCE_STATE_KEYS.enableRedundantTaskEvents,
      "false",
      true
    );
    setEnableRedundantTaskEvents(redundantEventsSetting === "true");
  };

  const checkPermissions = () => {
    // Matches boardPreferences updateSetting gate (grant_one)
    setCanEdit(!!user?.can(GRANTS.grant_one));
  };

  const handleSettingChange = (checked: boolean) => {
    if (!userPreferences || !canEdit) return;

    setEnableRedundantTaskEvents(checked);
    userPreferences.updateSetting(
      PERSISTENCE_STATE_KEYS.enableRedundantTaskEvents,
      String(checked),
      true
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">{subcategory.description}</p>
      </div>

      <div className="space-y-5 max-w-3xl">
        <Card className="py-3 px-4">
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-foreground mb-1">Task Event Placement</h3>
              <p className="text-xs text-muted-foreground">
                Board-wide rules for placing task events on the schedule
              </p>
            </div>

            <div className="flex items-start justify-between gap-6">
              <div className="flex-1 space-y-1">
                <Label htmlFor="enableRedundantTaskEvents" className="text-sm font-medium">
                  Enable redundant task events within time ranges
                </Label>
                <p className="text-xs text-muted-foreground">
                  Allow multiple events for the same task on overlapping dates. When off, placing a
                  duplicate removes the overlapping event for everyone on this board.
                </p>
              </div>
              <Switch
                id="enableRedundantTaskEvents"
                checked={enableRedundantTaskEvents}
                disabled={!canEdit}
                onCheckedChange={handleSettingChange}
              />
            </div>

            {!canEdit && (
              <p className="text-xs text-muted-foreground">
                You do not have permission to change this board setting.
              </p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ScheduleTaskEventsDetail;
